import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { NotificationService } from 'projects/shared/services/notification.service';
import { AssetService } from '../../services/asset-service';
import TruvBridge, { SuccessMetadata } from '@truv/bridge';
import { BorrowerVerificationService } from '../../services/borrower-verification.service';
import { EnrollmentEvent } from '../../models/borrower-verification-order-history.model';
import { firstValueFrom } from 'rxjs';
import { ArgyleService } from '../../services/argyle.service';
import { Connect, ConnectEventHandlers, ConnectOptions, ConnectDoneEvent, ConnectCancelEvent, ConnectErrorEvent } from 'connect-web-sdk';

@Component({
    selector: 'digital-assets-verification',
    templateUrl: './digital-assets-verification.component.html',
    styleUrls: ['./digital-assets-verification.component.scss'],
    standalone: false
})
export class DigitalAssetsVerificationComponent implements AfterViewInit, OnDestroy {

  @Input() applicationId: number;
  @Input() borrowerId: number;

  @Input() taskId: number | null | undefined;
  @Input() borrowerVerificationOrderId: number | null | undefined;

  @Input() provider: string = "FormFree";
  @Input() thirdPartyEnrollmentWidget: string = "";

  /**
   * True when thirdPartyEnrollmentWidget is an order token. The widget then runs in order mode and the verification is
   * only complete once the order itself completes - one connected institution is just one part of the order.
   */
  @Input() usesOrdersApi: boolean = false;

  @Output() complete: EventEmitter<any> = new EventEmitter<any>();
  @Output() error: EventEmitter<any> = new EventEmitter<any>();
  @Output() close: EventEmitter<any> = new EventEmitter<any>();
  @Output() cancel: EventEmitter<any> = new EventEmitter<any>();

  private _truvBridge: any;

  private _componentIsBeingDestroyed: boolean = false;

  private _linkCreated: boolean = false;
  private _verificationCompleted: boolean = false;

  /**
   * Latched for the duration of a completion attempt. Truv can report the same order as COMPLETED more than
   * once, and every `complete` emission advances the wizard a step, so a duplicate would skip one.
   */
  private _completionLatched: boolean = false;

  private _verificationMarkedAsComplete: boolean = false;

  constructor(
    private readonly _assetService: AssetService,
    private readonly _borrowerVerificationService: BorrowerVerificationService,
    private readonly _spinner: NgxSpinnerService,
    private readonly _notificationService: NotificationService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _argyleService: ArgyleService
  ) { }

  ngAfterViewInit(): void {
    if (this.provider == "FormFree") {
      this.initializeFormFree();
    }
    else if (this.provider == "Truv") {
      this.initializeTruvBridge();
    }
    else if (this.provider == "Argyle") {
      this.initializeArgyleLink();
    }
  }

  ngOnDestroy(): void {
    this._componentIsBeingDestroyed = true;
    this._truvBridge?.close();
    // Only tear down Connect when the Argyle/Connect flow actually launched it;
    // FormFree and Truv flows never call Connect.launch().
    if (this.provider === 'Argyle') {
      Connect.destroy();
    }
  }

  private initializeFormFree = () => {
    let htmlWidgetSrc = this.thirdPartyEnrollmentWidget;
    htmlWidgetSrc = htmlWidgetSrc.replace(
      'element.parentNode.removeChild(element);',
      ''
    );

    document.getElementById("digital-assets-order-container").innerHTML = htmlWidgetSrc
    window.addEventListener('message', this.messageListener);
  }

  private initializeTruvBridge = () => {
    setTimeout(() => {
      this._truvBridge = TruvBridge.init({
        bridgeToken: this.thirdPartyEnrollmentWidget,
        isOrder: this.usesOrdersApi,
        position: {
          type: 'inline',
          container: document.querySelector('#digital-assets-order-container')
        },
        onLoad: () => {
          // Optional, called when Bridge loads
          console.log('Bridge loaded')
        },
        onSuccess: (public_token: string, metadata: SuccessMetadata) => {
          console.log('success handler')
          // Send the public_token to your server to exchange for an access_token
          // and retrieve payroll data.
          // The metadata object contains info about the Link.
          console.log("token: ", public_token)
          console.log("metadata: ", metadata)
        },
        onEvent: async (event_type: string, payload: any, source?: string) => {
          // all events fire this function. event_type indicates what the event is,
          // payload has additional information depending on the event. In order mode
          // Bridge passes the emitting surface ("order" page vs "bridge" widget) as a third argument.
          console.log('event: ', event_type)
          console.log('payload: ', payload)

          if (event_type == "LINK_CREATED") {
            // In order mode a created link is one institution of the order, not the end of the verification.
            this._linkCreated = true;
          } else if (event_type == "COMPLETED" && this.usesOrdersApi && source == "order") {
            await this.postEnrollmentEvent(event_type, payload);
            await this.markVerificationAsComplete();
            return;
          } else if (event_type == "CLOSE" && !this.usesOrdersApi) {
            if (this._linkCreated) {
              await this.markVerificationAsComplete();
            }
          } else if (event_type == "ERROR") {
            this.handleTruvError(payload);
          }

          await this.postEnrollmentEvent(event_type, payload);
        },
        onClose: () => {
          // Optional, called when Bridge is closed by the user.
          console.log('Bridge closed');
          if (this._componentIsBeingDestroyed) {
            return;
          }

          // `complete` already advanced the caller; emitting `close` on the way out would advance it again.
          if (this._verificationCompleted) {
            return;
          }

          // Order mode reports its own completion, so closing before that just leaves the task open to resume.
          if (!this.usesOrdersApi && this._linkCreated) {
            return;
          }

          this.close.emit();
          this._cdr.detectChanges();
        }
      });

      this._truvBridge.open();
    }, 100);
  }

  private handleTruvError = (payload: any) => {
    const errorType = payload?.error?.error_type;
    console.error('Bridge error: ', payload);

    if (errorType == 'SESSION_EXPIRED') {
      this._notificationService.showError(
        "Your verification session expired. Please reopen the task to continue.",
        "Session Expired");
      return;
    }

    this._notificationService.showError(
      payload?.error?.error_message || "An error occurred during verification. Please try again.",
      "Verification Error");
  }

  private postEnrollmentEvent = async (eventType: string, payload: any) => {
    if (!this.borrowerVerificationOrderId) return;
    try {
      const event: EnrollmentEvent = {
        eventName: eventType,
        eventData: JSON.stringify(payload),
        eventDateTime: new Date().toISOString()
      };
      await firstValueFrom(this._borrowerVerificationService.postEnrollmentEvent(this.borrowerVerificationOrderId, event));
    } catch (error) {
      console.error('Failed to post enrollment event', error);
    }
  }

  private markVerificationAsComplete = async () => {
    if (this._completionLatched) {
      return;
    }
    this._completionLatched = true;

    try {
      if (this.taskId) {
        await firstValueFrom(this._borrowerVerificationService.markBorrowerVerificationTaskComplete(this.taskId));
      } else {
        await firstValueFrom(this._assetService.markFormFreeForBorrowerComplete(this.borrowerId, this.applicationId));
      }
      this._verificationCompleted = true;
      this.complete.emit();
    } catch (error) {
      // A genuine failure should not block a later COMPLETED from retrying.
      this._completionLatched = false;
      this._notificationService.showError(error.message || "An error occurred while sharing assets digitally.", "Error!");
      this.error.emit();
    } finally {
      this._spinner.hide();
      this._cdr.detectChanges();
    }
  }

  private initializeArgyleLink = () => {
    const connectUrl = this.thirdPartyEnrollmentWidget;

    const connectEventHandlers: ConnectEventHandlers = {
      onDone: async (event: ConnectDoneEvent) => {
        console.log('Connect onDone:', event);
        this._linkCreated = true;
        await this.postEnrollmentEvent('DONE', event);
        await this.markVerificationAsComplete();
      },
      onCancel: async (event: ConnectCancelEvent) => {
        console.log('Connect onCancel:', event);
        await this.postEnrollmentEvent('CANCEL', event);
        if (!this._componentIsBeingDestroyed) {
          this.close.emit();
          this._cdr.detectChanges();
        }
      },
      onError: async (event: ConnectErrorEvent) => {
        console.error('Connect onError:', event);
        await this.postEnrollmentEvent('ERROR', event);
        this._notificationService.showError(
          event.reason || 'An error occurred during asset verification.',
          'Verification Error'
        );
      },
      onLoad: () => {
        console.log('Connect loaded');
      },
      onRoute: (event) => {
        console.log('Connect onRoute:', event);
      },
      onUser: (event) => {
        console.log('Connect onUser:', event);
      }
    };

    const connectOptions: ConnectOptions = {
      selector: '#digital-assets-order-container'
    };

    Connect.launch(connectUrl, connectEventHandlers, connectOptions);
  }

  private messageListener = async (msg: any) => {
    // Doing this to avoid multiple calls to markVerificationAsComplete, since I saw this message being sent multiple times
    if (msg.data === 'closeFrame' && !this._verificationMarkedAsComplete) {
      await this.markVerificationAsComplete();
      this._verificationMarkedAsComplete = true;

      window.removeEventListener('message', this.messageListener);
    }
  };
}
